/**
 * AdminAPI — API client for admin endpoints
 */

import { loadAccessToken } from './NetworkManager';

const SERVER_URL = import.meta.env.VITE_SERVER_URL as string || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://localhost:8080'
);

// --- Types ---

export interface CardDefinition {
  id: string;
  code: string;
  name: string;
  faction: string;
  faction_zh: string;
  type: string;
  type_zh: string;
  atk: number | null;
  score: number | null;
  sacrifice: number | null;
  rarity: string;
  effect: string;
  special_type: string;
  pack: string;
  image_url: string;
  is_active: boolean;
  parsed_keywords: string[] | null;
  parsed_effects: ParsedEffects | null;
  parse_status: string;
  created_at: string;
  updated_at: string;
}

export interface ParsedEffects {
  triggers?: string[];
  actions?: ParsedAction[];
  keywords?: string[];
}

export interface ParsedAction {
  type: string;
  value?: number;
  string_value?: string;
  keyword?: string;
  selector?: {
    zone: string;
    side: string;
    count: number;
    requires_choice?: boolean;
  };
}

export interface KeywordInfo {
  id: string;
  name: string;
  desc: string;
}

export interface CardPack {
  id: string;
  name: string;
  description: string;
  price: number;
  cards_per_pack: number;
  is_active: boolean;
  weights: CardPackWeight[];
  created_at: string;
  updated_at: string;
}

export interface CardPackWeight {
  id: string;
  pack_id: string;
  rarity: string;
  weight: number;
}

export interface PlayerWallet {
  player_id: string;
  coins: number;
  updated_at: string;
}

export interface CollectionSummary {
  card_code: string;
  count: number;
}

export interface AdminPlayerDetail {
  player: {
    id: string;
    username: string;
    mmr: number;
    level: number;
    xp: number;
    wins: number;
    losses: number;
    created_at: string;
    updated_at: string;
  };
  wallet: PlayerWallet | null;
  collection: CollectionSummary[];
  total_cards: number;
  recent_games: unknown[];
}

export interface AdminStats {
  online_count: number;
  total_games: number;
  total_players: number;
  top_players: {
    id: string;
    username: string;
    mmr: number;
    level: number;
    wins: number;
    losses: number;
  }[];
}

export interface PaginatedCards {
  cards: CardDefinition[];
  total_count: number;
  offset: number;
  limit: number;
}

export interface PaginatedPlayers {
  players: {
    id: string;
    username: string;
    mmr: number;
    level: number;
    xp: number;
    wins: number;
    losses: number;
    created_at: string;
    updated_at: string;
  }[];
  total_count: number;
  offset: number;
  limit: number;
}

// --- Helpers ---

class AdminApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
  }
}

async function adminRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = loadAccessToken();
  if (!token) {
    throw new AdminApiError(401, '未登入');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const res = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data: unknown = await res.json();

  if (!res.ok) {
    const errorData = data as { error?: string };
    throw new AdminApiError(res.status, errorData.error ?? '未知錯誤');
  }

  return data as T;
}

// --- Card APIs ---

export async function listCards(offset = 0, limit = 50, search = ''): Promise<PaginatedCards> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (search) params.set('search', search);
  return adminRequest<PaginatedCards>('GET', `/api/admin/cards?${params.toString()}`);
}

export async function createCard(card: Partial<CardDefinition>): Promise<CardDefinition> {
  return adminRequest<CardDefinition>('POST', '/api/admin/cards', card);
}

export async function updateCard(code: string, card: Partial<CardDefinition>): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('PUT', `/api/admin/cards/${code}`, card);
}

export async function deleteCard(code: string): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('DELETE', `/api/admin/cards/${code}`);
}

export async function importCards(cards: unknown[]): Promise<{ imported: number; total: number }> {
  return adminRequest<{ imported: number; total: number }>('POST', '/api/admin/cards/import', { cards });
}

export async function importFromSheet(sheetId: string, sheetRange = 'Sheet1!A:Z'): Promise<{ imported: number; total: number }> {
  return adminRequest<{ imported: number; total: number }>('POST', '/api/admin/cards/import-sheet', {
    sheet_id: sheetId,
    sheet_range: sheetRange,
  });
}

// --- Pack APIs ---

export async function listPacks(): Promise<CardPack[]> {
  return adminRequest<CardPack[]>('GET', '/api/admin/packs');
}

export async function createPack(pack: {
  name: string;
  description: string;
  price: number;
  cards_per_pack: number;
  weights: { rarity: string; weight: number }[];
}): Promise<CardPack> {
  return adminRequest<CardPack>('POST', '/api/admin/packs', pack);
}

export async function updatePack(id: string, pack: Partial<CardPack>): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('PUT', `/api/admin/packs/${id}`, pack);
}

export async function deletePack(id: string): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('DELETE', `/api/admin/packs/${id}`);
}

// --- Player APIs ---

export async function listPlayers(offset = 0, limit = 50, search = ''): Promise<PaginatedPlayers> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  if (search) params.set('search', search);
  return adminRequest<PaginatedPlayers>('GET', `/api/admin/players?${params.toString()}`);
}

export async function getPlayerDetail(id: string): Promise<AdminPlayerDetail> {
  return adminRequest<AdminPlayerDetail>('GET', `/api/admin/players/${id}`);
}

// --- Stats API ---

export async function getStats(): Promise<AdminStats> {
  return adminRequest<AdminStats>('GET', '/api/admin/stats');
}

// --- Card Effects APIs ---

export async function updateCardEffects(code: string, data: {
  parsed_keywords: string[];
  parsed_effects: ParsedEffects;
  parse_status: string;
}): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('PUT', `/api/admin/cards/${code}/effects`, data);
}

export async function reparseCard(code: string): Promise<{ status: string }> {
  return adminRequest<{ status: string }>('POST', `/api/admin/cards/${code}/reparse`);
}

export async function reparseAllCards(): Promise<{ status: string; reparsed: number }> {
  return adminRequest<{ status: string; reparsed: number }>('POST', '/api/admin/cards/reparse-all');
}

export async function listKeywords(): Promise<KeywordInfo[]> {
  return adminRequest<KeywordInfo[]>('GET', '/api/admin/keywords');
}

// --- Admin Check ---

/**
 * Verify the current user has admin access by calling the stats endpoint.
 * Returns true if admin, false if 403/401, throws on other errors.
 */
export async function checkAdminAccess(): Promise<boolean> {
  try {
    await adminRequest<AdminStats>('GET', '/api/admin/stats');
    return true;
  } catch (err) {
    if (err instanceof AdminApiError && (err.status === 403 || err.status === 401)) {
      return false;
    }
    throw err;
  }
}
